import { useMemo, useRef, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler } from 'chart.js';
import {
  formatParseMinimalDate,
  formatShortMinimalDate,
  formatParseDayOfWeek,
  formatParseFullDateOfWeek,
} from '/src/utils/date';
import { SkeletonChart } from '/src/components/SkeletonChart';
import { getCountryName, getLanguageName, humanizeMetric, humanizeDimension, percentizeValue } from '../utils';
import { getRowColor } from './row-color';
import { ChartLegend } from './chart-legend';
import { useAtom } from 'jotai';
import { columnOrderAtom, lastValidOrderAtom } from './table';
import { memo } from 'react';
import { hexToRgba } from '/src/utils/color';
import { useQueryParams, useSelectedSite } from '/src/hooks';
import { ColumnSelector } from './column-selector';
import { GoalSelector } from './goal-selector';
import { METRICS, CHART_MODES, OTHERS, TOOLTIP_POSITIONS, CHART_POSITION } from '../constants';
import { abbreviateNumber } from '/src/utils';
import classNames from 'classnames';

ChartJS.defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';
ChartJS.defaults.font.size = 14;

const activeLineOnTop = {
  id: 'activeLineOnTop',
  afterDatasetsDraw(chart) {
    const activeIdx =
      chart.$fromLegend && Number.isInteger(chart.$fromLegend.datasetIndex)
        ? chart.$fromLegend.datasetIndex
        : chart.getActiveElements()?.[0]?.datasetIndex;

    if (!Number.isInteger(activeIdx)) return;

    const meta = chart.getDatasetMeta(activeIdx);
    if (!meta || meta.hidden) return;

    const { ctx, chartArea } = chart;

    ctx.save();
    const prevFill = meta.dataset.options.fill;
    meta.dataset.options.fill = false;

    const prevWidth = meta.dataset.options.borderWidth;
    meta.dataset.options.borderWidth = (typeof prevWidth === 'number' ? prevWidth : 2) + 1;

    meta.dataset.draw(ctx, chartArea);

    meta.data?.forEach((el) => el.draw(ctx, chartArea));

    meta.dataset.options.fill = prevFill;
    meta.dataset.options.borderWidth = prevWidth;

    ctx.restore();
  },
};

ChartJS.register(Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler);

function ChartLabel({ mode, metric, dimension, className }) {
  const label = [CHART_MODES.TOTAL, CHART_MODES.VERTICAL, CHART_MODES.HORIZONTAL].includes(mode)
    ? `Total`
    : mode === CHART_MODES.PERCENT
      ? `Percentage of`
      : '';

  return (
    <span className={classNames('flex items-center space-x-[5px] ml-2.5', className)}>
      <h2 className="text-black-pearl-500 text-[22px] font-semibold">
        <span>{label}</span> <span className="capitalize">{humanizeMetric(metric)}</span>
      </h2>
      <h3 className="text-[#4b4b4b] text-opacity-50 text-xl font-semibold gap-5">by {humanizeDimension(dimension)}</h3>
    </span>
  );
}

export function TimeChart({ selected, data = [], conversions = [], isLoading }) {
  const [column, setOrder] = useAtom(columnOrderAtom);
  const [lastValidOrder, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const { get: getParam } = useQueryParams();

  const chartMode = getParam('over_time_graph', CHART_MODES.PERCENT);
  const bothMode = [CHART_MODES.HORIZONTAL, CHART_MODES.VERTICAL].includes(getParam('both')) ? getParam('both') : false;

  const mode = bothMode ? bothMode : chartMode;

  const metric = lastValidOrder || METRICS.VISITORS;

  return (
    <div className="flex w-full flex-col items-start border bg-white rounded-[5px] border-mystic-500 p-5 shadow-sm min-h-[437px]">
      <div className="flex w-full justify-between items-center">
        <span>&nbsp;</span>
        <div className="flex space-x-4 items-center">
          <GoalSelector
            onSelect={(_goal) => {
              // TODO: update query params
            }}
          />
          <ColumnSelector
            dimension={selected}
            selected={lastValidOrder}
            onSelect={(newColumn) => {
              setOrder({ id: newColumn, desc: column.desc });
              setLastValidOrder(newColumn);
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <SkeletonChart className="flex-1" />
      ) : (
        <div className="flex-1 h-full w-full mt-[5px] -ml-2">
          <Chart data={data} conversions={conversions} dimension={selected} metric={metric} mode={mode} />
        </div>
      )}
    </div>
  );
}

function formatLabel(name, type, { domain, siteName }) {
  if (type === 'countries') {
    return getCountryName(name);
  }
  if (type === 'languages') {
    return getLanguageName(name);
  }
  if (name === '' && domain === '' && /page/i.test(type)) {
    return siteName;
  }
  if (domain && domain.length > 0) {
    return `${domain}${name}`;
  }
  return name;
}

function showTooltipForDataset(chart, datasetIndex, anchor = TOOLTIP_POSITIONS.LAST) {
  const meta = chart.getDatasetMeta(datasetIndex);
  if (!meta || !meta.data?.length) return;

  // activate all points in the dataset
  const active = meta.data.map((_, i) => ({ datasetIndex, index: i }));

  // place tooltip at anchor
  const index =
    anchor === TOOLTIP_POSITIONS.FIRST
      ? 0
      : anchor === TOOLTIP_POSITIONS.MIDDLE
        ? Math.floor(meta.data.length / 2)
        : meta.data.length - 1;

  const el = meta.data[index];
  if (!el) return;

  chart.$fromLegend = { datasetIndex, index };

  chart.setActiveElements(active);
  chart.tooltip?.setActiveElements([{ datasetIndex, index }], {
    x: el.x,
    y: el.y,
  });

  chart.update('none');
}

function hideTooltip(chart) {
  if (!chart) return;
  chart.$fromLegend = undefined;
  chart.setActiveElements([]);
  chart.tooltip?.setActiveElements([], { x: 0, y: 0 });

  chart.update('none');
}

function syncHover(sourceChart, targetChart, event) {
  const points = sourceChart.getElementsAtEventForMode(event, 'index', { intersect: false });

  if (points.length) {
    targetChart.setActiveElements(points);
    // targetChart.tooltip.setActiveElements(points, { x: 0, y: 0 });
    targetChart.update();
  } else {
    targetChart.setActiveElements([]);
    // targetChart.tooltip.setActiveElements([], { x: 0, y: 0 });
    targetChart.update();
  }
}

function clearHover(chart) {
  chart.setActiveElements([]);
  chart.tooltip.setActiveElements([], { x: 0, y: 0 });
  chart.update();
}

const Chart = memo(function Chart({ data = [], conversions = [], dimension, metric, mode }) {
  const chartRef = useRef(null);
  const topChartRef = useRef(null);
  const { selectedSite } = useSelectedSite();
  const { get: getParam } = useQueryParams();

  const excludeOthers = getParam('exclude_others', 'false') === 'true';
  const isPercentageBars = getParam('percentage_bars', 'false') === 'true';

  const orderedData = useMemo(() => {
    if (excludeOthers) {
      return data.filter((d) => d.name !== OTHERS);
    }

    // use the order prop to rearrange
    const reordered = data.slice().sort((a, b) => {
      const isAOthers = a.name === OTHERS;
      const isBOthers = b.name === OTHERS;

      if (isAOthers && !isBOthers) return 1;
      if (!isAOthers && isBOthers) return -1;
      if ((isAOthers && isBOthers) || (!isAOthers && !isBOthers)) return 0;
    });

    return reordered;
  }, [data, excludeOthers]);

  const maxDailySum = useMemo(() => {
    const sumsByDate = orderedData.reduce((acc, series) => {
      series.dailyData.forEach(({ date, value }) => {
        acc[date] = (acc[date] ?? 0) + (value ?? 0);
      });
      return acc;
    }, {});
    const sums = Object.values(sumsByDate);
    return sums.length ? Math.max(...sums) : 0;
  }, [orderedData]);

  const labels = useMemo(() => {
    return orderedData?.[0]?.dailyData?.map?.((d) => d.date) ?? [];
  }, [orderedData]);

  const conversionsDataset = useMemo(() => {
    if (!conversions.length || !labels?.length) return null;

    const byDate = new Map(conversions.map(({ date, value }) => [date, value ?? 0]));
    const data = labels.map((d) => byDate.get(d) ?? 0);

    const baseColor = '#3B82F6';

    return {
      label: 'Conversions',
      data,
      borderColor: baseColor,
      backgroundColor: hexToRgba(baseColor, 0.1),
      borderWidth: 2,
      borderDash: [4, 2],
      borderDashOffset: 0,
      pointRadius: labels.length === 1 ? 5 : 0,
      pointHoverRadius: 5,
      pointStyle: 'circle',
      pointBackgroundColor: baseColor,
      pointBorderColor: '#fff',
      pointHoverBorderColor: '#fff',
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,
      pointHitRadius: 3,
      yAxisID: 'y2', // right side axis
      fill: false,
      clip: false,
      tension: 0.3,
    };
  }, [conversions, labels]);

  const datasetFor = useCallback(
    (orderedData, selectedSite, dimension, mode) => {
      return orderedData
        .map((entry, idx) => {
          const rawValues = entry.dailyData.map((d) => ({
            value: d.value,
            prevValue: d.prevValue,
            pctChange: d.pctChange,
          }));

          if (rawValues.length === 0) return null;

          const parsedValues = entry.dailyData.map((point, index) => {
            if (mode === CHART_MODES.PERCENT) {
              const totalForDate = orderedData.reduce((sum, series) => {
                return sum + (series.dailyData?.[index]?.value ?? 0);
              }, 0);

              const value = point.value;
              return totalForDate > 0 ? (value / totalForDate) * 100 : 0;
            }
            if (mode === CHART_MODES.TOTAL) {
              return point.value;
            }
          });

          const label =
            entry.name === OTHERS
              ? 'Others'
              : formatLabel(entry.name, dimension, { domain: entry.domain, siteName: selectedSite.name });

          const baseColor = getRowColor(entry.name === OTHERS ? -1 : idx);
          // const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
          // gradient.addColorStop(0, hexToRgba(baseColor, 0.15));
          // gradient.addColorStop(1, hexToRgba(baseColor, 0));

          return {
            label,
            data: parsedValues,
            raw: rawValues,
            borderColor: baseColor,
            backgroundColor: hexToRgba(baseColor, 0.1),
            fill: idx === 0 ? true : '-1',
            ...(isPercentageBars
              ? {
                  type: mode === CHART_MODES.PERCENT ? 'bar' : 'line',
                  borderColor: baseColor,
                  backgroundColor: hexToRgba(baseColor, 0.3),
                  fill: mode === CHART_MODES.PERCENT ? false : idx === 0 ? true : '-1',
                  // Bar specific niceties
                  ...(mode === CHART_MODES.PERCENT
                    ? {
                        stack: 'pct',
                        borderWidth: 0,
                        borderRadius: 3,
                        ...(isPercentageBars
                          ? {
                              categoryPercentage: 0.8,
                              barPercentage: 0.9,
                              // maxBarThickness: 28
                            }
                          : {}),
                      }
                    : {}),
                }
              : {}),
            // tension: 0.4,
            pointRadius: labels.length === 1 ? 5 : 0,
            pointHoverRadius: 5,
            pointStyle: 'circle',
            pointBackgroundColor: baseColor,
            pointBorderColor: '#fff',
            pointHoverBorderColor: '#fff',
            pointBorderWidth: 2,
            pointHoverBorderWidth: 2,
            pointHitRadius: 3,
            borderWidth: 2,
            clip: false,
            // tension: mode === CHART_MODES.TOTAL ? 0.4 : undefined,
          };
        })
        .filter(Boolean);
    },
    [labels.length, isPercentageBars],
  );

  const datasets = useMemo(() => {
    if ([CHART_MODES.HORIZONTAL, CHART_MODES.VERTICAL].includes(mode)) {
      return [
        datasetFor(orderedData, selectedSite, dimension, CHART_MODES.TOTAL),
        datasetFor(orderedData, selectedSite, dimension, CHART_MODES.PERCENT),
      ];
    }
    return datasetFor(orderedData, selectedSite, dimension, mode);
  }, [orderedData, selectedSite, dimension, mode, datasetFor]);

  const optionsFor = useCallback(
    (labels, metric, mode, maxDailySum, position = null, bothMode = null, hasConversions = false) => {
      const isPercentages = mode === CHART_MODES.PERCENT;
      const isBoth = !!position;
      const isTop = isBoth && position === CHART_POSITION.TOP;
      const isLeft = isBoth && position === CHART_POSITION.LEFT;
      const isVertical = bothMode === CHART_MODES.VERTICAL;
      const isHorizontal = bothMode === CHART_MODES.HORIZONTAL;

      const hideXAxisLabels = isBoth && isVertical && isTop;
      const hideYAxisLabels = isBoth && isHorizontal && isLeft;

      return {
        responsive: true,
        interaction: {
          // intersect: true,
          mode: 'index', // : 'dataset',
          intersect: false,
        },
        maintainAspectRatio: false,
        spanGaps: true,
        scales: {
          y: hideYAxisLabels
            ? { display: false }
            : isPercentages && isPercentageBars
              ? {
                  type: 'linear',
                  min: 0,
                  max: 100,
                  stacked: true,
                  ticks: {
                    stepSize: 20,
                    callback: (v) => `${v}%`,
                    color: 'rgb(104, 118, 141)',
                  },
                  grid: { color: 'rgba(0,0,0,0.06' },
                }
              : yAxisConfigFromMax(maxDailySum, isPercentages),
          ...(hasConversions
            ? {
                y2: hideYAxisLabels
                  ? { display: false }
                  : {
                      type: 'linear',
                      min: 0,
                      position: 'right',
                      grid: { drawOnChartArea: false },
                      ticks: {
                        color: 'rgb(104, 118, 141)',
                        precision: 0, // whole numbers
                        callback: (v) => abbreviateNumber(v),
                      },
                      title: {
                        display: true,
                        text: 'Conversions',
                        color: 'rgb(104, 118, 141)',
                        font: { size: 13, weight: '600' },
                        padding: { bottom: 8 },
                      },
                    },
              }
            : {}),
          x: hideXAxisLabels
            ? { display: false }
            : {
                ticks: {
                  autoSkip: labels.length <= 10 ? false : undefined,
                  color: 'rgb(104, 118, 141)',
                  padding: 15,
                  maxTicksLimit: 7,
                  callback: function (value, _index, _values) {
                    const rawLabel = this.getLabelForValue(value);
                    if (rawLabel && rawLabel !== '') {
                      const dayOfWeek = formatParseDayOfWeek(rawLabel);
                      const date = formatShortMinimalDate(rawLabel);
                      return [dayOfWeek, date];
                    }
                    return rawLabel;
                  },
                },
                grid: {
                  display: false,
                },
                stacked: true,
                offset: isPercentageBars ? true : labels.length === 1 ? true : undefined,
              },
        },
        plugins: {
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: (ctx) => externalTooltipHandler(ctx, metric, isPercentages),
            callbacks: {
              title: (items) => {
                const itemDate = labels.filter(Boolean).at(items[0].dataIndex);
                return formatParseMinimalDate(itemDate);
              },
            },
          },
          legend: {
            display: false,
          },
          // mouseline: {
          //   enabled: true,
          // },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
        },
      };
    },
    [isPercentageBars],
  );

  const hasConversions = !!conversionsDataset;

  const options = useMemo(() => {
    if ([CHART_MODES.HORIZONTAL, CHART_MODES.VERTICAL].includes(mode)) {
      return [
        optionsFor(labels, metric, CHART_MODES.TOTAL, maxDailySum, CHART_POSITION.TOP, mode, hasConversions),
        optionsFor(labels, metric, CHART_MODES.PERCENT, maxDailySum, CHART_POSITION.BOTTOM, mode, hasConversions),
      ];
    }
    return optionsFor(labels, metric, mode, maxDailySum, null, null, hasConversions);
  }, [labels, metric, mode, maxDailySum, optionsFor, hasConversions]);

  const chartData = useMemo(() => {
    if ([CHART_MODES.HORIZONTAL, CHART_MODES.VERTICAL].includes(mode)) {
      const [totalDs, percentDs] = datasets;
      return {
        labels,
        datasets: [conversionsDataset ? [...totalDs, conversionsDataset] : totalDs, percentDs],
      };
    }

    return {
      labels,
      datasets: conversionsDataset ? [...datasets, conversionsDataset] : datasets,
    };
  }, [labels, datasets, conversionsDataset, mode]);

  useEffect(() => {
    const top = topChartRef.current;
    const bottom = chartRef.current;
    if (!top || !bottom) return;

    top.options.onHover = (evt) => syncHover(top, bottom, evt);
    bottom.options.onHover = (evt) => syncHover(bottom, top, evt);

    // sync mouse out
    const handleTopOut = () => clearHover(bottom);
    const handleBottomOut = () => clearHover(top);

    if (top.canvas && bottom.canvas) {
      top.canvas.addEventListener('mouseout', handleTopOut);
      bottom.canvas.addEventListener('mouseout', handleBottomOut);
    }

    return () => {
      if (top.canvas && bottom.canvas) {
        top.canvas.removeEventListener('mouseout', handleTopOut);
        bottom.canvas.removeEventListener('mouseout', handleBottomOut);
      }
    };
  }, []);

  return (
    <>
      {[CHART_MODES.HORIZONTAL, CHART_MODES.VERTICAL].includes(mode) ? (
        <div
          className={classNames('flex', {
            'flex-col': mode === CHART_MODES.VERTICAL,
          })}
        >
          <div className={classNames('flex flex-col', { 'w-1/2': mode === CHART_MODES.HORIZONTAL })}>
            <ChartLabel mode={CHART_MODES.TOTAL} metric={metric} dimension={dimension} />

            <div
              className={classNames('relative', {
                'w-[calc(100%-45px)] h-[175px]': mode === CHART_MODES.VERTICAL,
                'h-[400px]': mode === CHART_MODES.HORIZONTAL,
              })}
            >
              <Line
                ref={topChartRef}
                data={{ labels: chartData.labels, datasets: chartData.datasets[0] }}
                options={options[0]}
                plugins={[activeLineOnTop]}
              />
            </div>
          </div>

          <div className={classNames('flex flex-col', { 'w-1/2': mode === CHART_MODES.HORIZONTAL })}>
            <ChartLabel mode={CHART_MODES.PERCENT} metric={metric} dimension={dimension} />

            <div
              className={classNames('relative w-full', {
                'h-[225px]': mode === CHART_MODES.VERTICAL,
                'h-[400px]': mode === CHART_MODES.HORIZONTAL,
              })}
            >
              <Line
                ref={chartRef}
                data={{ labels: chartData.labels, datasets: chartData.datasets[1] }}
                options={options[1]}
                plugins={[activeLineOnTop]}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <ChartLabel mode={mode} metric={metric} dimension={dimension} />

          <div className="relative w-full h-[400px]">
            <Line ref={chartRef} data={chartData} options={options} plugins={[activeLineOnTop]} />
          </div>
        </>
      )}
      <ChartLegend
        chartRef={chartRef}
        onMouseEnter={(e) => {
          const chart = chartRef.current;
          if (!chart) return;

          const idx = Number(e.currentTarget?.dataset.index);
          if (Number.isNaN(idx)) return;

          showTooltipForDataset(chart, idx, TOOLTIP_POSITIONS.MIDDLE);
        }}
        onMouseLeave={() => {
          hideTooltip(chartRef.current);
        }}
      />
    </>
  );
});

const externalTooltipHandler = (context, metric = METRICS.VISITORS, isPercentages) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);
  const fromLegend = chart.$fromLegend;

  if (fromLegend || !tooltip || tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  const xScale = chart.scales.x;

  // if (fromLegend && Number.isInteger(fromLegend.datasetIndex)) {
  //   ({ datasetIndex, index: dataIndex } = chart.$fromLegend);
  // } else {
  // normal hover path
  const pointXIdx = xScale.getValueForPixel(tooltip.caretX);

  const allDatapointsAtX = chart.data.datasets.map((ds) => {
    const { value, prevValue, pctChange } = ds.raw?.[pointXIdx] ?? {};
    const isConversionRow = ds.label === 'Conversions';

    return {
      label: ds.label,
      color: ds.borderColor,
      percentage: isConversionRow ? undefined : ds.data?.[pointXIdx],
      value: isConversionRow ? ds.data?.[pointXIdx] : value,
      prevValue,
      pctChange: prevValue === 0 && value > 0 ? 100 : pctChange,
    };
  });

  if (!allDatapointsAtX.length) {
    tooltip.style.opacity = 0;
    return;
  }

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  const rowGroups = [];

  for (const point of allDatapointsAtX) {
    const isConversionRow = point.label === 'Conversions';
    const rowGroup = document.createElement('div');

    Object.assign(rowGroup.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    });

    if (point.label) {
      const titleLabel = document.createElement('div');
      titleLabel.textContent = point.label;
      Object.assign(titleLabel.style, {
        fontWeight: '600',
        fontSize: '14px',
        color: point.color ?? '#0a1c2e',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        minWidth: '0',
        maxWidth: '235px',
      });
      rowGroup.appendChild(titleLabel);
    }

    const tooltipBody = document.createElement('div');

    Object.assign(tooltipBody.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });

    const metricGroup = document.createElement('div');
    Object.assign(metricGroup.style, {
      display: 'flex',
      gap: '2px',
    });

    const metricRow = document.createElement('div');
    Object.assign(metricRow.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
      textTransform: 'lowercase',
    });

    const valueText = document.createElement('span');
    const valueLabel = abbreviateNumber(point.value);
    valueText.textContent = `${valueLabel}${isPercentages && !!point.percentage ? ` (${point.percentage.toFixed(1)}%)` : ''}`;
    Object.assign(valueText.style, {
      fontSize: '12px',
      fontWeight: '600',
      color: point.color,
    });

    const metricLabel = document.createElement('span');
    metricLabel.textContent = isConversionRow ? '' : humanizeMetric(metric);
    Object.assign(metricLabel.style, {
      fontSize: '12px',
      fontWeight: '400',
      color: 'rgba(75, 75, 75, 0.7)',
    });

    const changeLabel = document.createElement('span');
    changeLabel.textContent = isConversionRow ? '' : percentizeValue(point.pctChange, 0, false);
    Object.assign(changeLabel.style, {
      fontSize: '12px',
      fontWeight: '400',
      color: !point.pctChange || point.pctChange == 0 ? 'rgba(75, 75, 75, 0.9)' : point.pctChange > 0 ? 'green' : 'red',
    });

    metricRow.appendChild(valueText);
    metricRow.appendChild(metricLabel);
    metricRow.appendChild(changeLabel);

    metricGroup.appendChild(metricRow);
    tooltipBody.appendChild(metricGroup);

    rowGroup.appendChild(tooltipBody);

    rowGroups.push(rowGroup);
  }

  const contentContainer = tooltipEl.querySelector('.tooltip-content');

  if (contentContainer) {
    const dateLabel = document.createElement('div');

    const date = tooltip.dataPoints?.[pointXIdx]?.label;

    dateLabel.textContent = date ? formatParseFullDateOfWeek(date) : '';

    Object.assign(dateLabel.style, {
      fontSize: '13px',
      fontWeight: '400',
      color: 'rgba(10, 28, 46, 1)',
    });

    contentContainer.replaceChildren(dateLabel, ...rowGroups);
  }

  // Position relative to the chart's parent node
  const parent = chart.canvas.parentNode;
  const parentRect = parent.getBoundingClientRect();

  // base position in the parent's coordinate space
  let left = parentRect.left + tooltip.caretX;
  let top = parentRect.top + tooltip.caretY;

  // keep within parent horizontally
  const maxLeft = parentRect.right - tooltipEl.offsetWidth - 6;
  const minLeft = parentRect.left + 6;
  left = Math.max(minLeft, Math.min(maxLeft, left));

  // Display, position, and set styles for font
  Object.assign(tooltipEl.style, {
    position: 'absolute',
    opacity: 1,
    left: left === maxLeft ? 'unset' : `${left + 15}px`,
    right: left === maxLeft ? `${parentRect.right - tooltip.caretX - 130}px` : 'unset',
    top: `${top + 15}px`,
    font: tooltip.options.bodyFont.string,
    whiteSpace: 'nowrap',
    borderWidth: '1px',
    borderColor: '#E1E1E1',
    borderLeftColor: allDatapointsAtX?.[0]?.color,
    borderLeftWidth: '3px',
    transform: 'none',
  });
  tooltipEl.classList.add('shadow-lg', 'border', 'border-mystic-500', 'rounded', 'overflow-hidden', 'p-2.5');
};

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div.chartjs-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add('chartjs-tooltip');
    tooltipEl.style.cssText = `
      background: #fff;
      box-shadow: 0 0 0 1px rgba(225, 225, 225, 1) , 0 4px 10px 0 rgba(0, 0, 0, 0.1);
      min-width: 150px;
      border-radius: 5px;
      color: #000;
      opacity: 1;
      pointer-events: none;
      position: absolute;
      transition: all .1s ease;
      z-index: 100;
    `;

    const contentContainer = document.createElement('div');
    contentContainer.classList.add('tooltip-content');
    tooltipEl.appendChild(contentContainer);

    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

function yAxisConfigFromMax(maxVal, isPercentages) {
  if (isPercentages) {
    return {
      suggestedMin: 0,
      suggestedMax: 100,
      max: 100,
      ticks: {
        color: 'rgb(104, 118, 141)',
        stepSize: 25,
        padding: 10,
        maxTicksLimit: 5,
        callback: (v) => `${v}%`,
      },
      grid: {
        color: 'rgb(225, 230, 239)',
        lineWidth: 1,
      },
      border: {
        display: false,
      },
      stacked: true,
    };
  }

  const targetTicks = 6;
  const safeMax = Number.isFinite(maxVal) && maxVal > 0 ? maxVal : 1;
  const roughStep = Math.max(1, safeMax / targetTicks);
  const mag = 10 ** Math.floor(Math.log10(roughStep));
  const norm = roughStep / mag;
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  const stepSize = nice * mag;
  let suggestedMax = Math.ceil(safeMax / stepSize) * stepSize;
  if (suggestedMax === 0) suggestedMax = stepSize;

  return {
    suggestedMin: 0,
    suggestedMax,
    ticks: {
      color: 'rgb(104, 118, 141)',
      stepSize,
      maxTicksLimit: targetTicks,
      padding: 10,
      callback: (v) => abbreviateNumber(v),
    },
    grid: { color: 'rgb(225, 230, 239)', lineWidth: 1 },
    border: { display: false },
    stacked: true,
  };
}
